// Site builder state — owns the active template selection and the reactive content
// objects that BuilderView edits. Two content objects are maintained:
//   builderContent — the template's default content; acts as the merge base.
//   content        — the working copy shown in the builder (template defaults merged with saved DB values).
// This separation lets replaceContent() always merge saved data correctly even after a template switch.
import { reactive } from 'vue'
import { API_BASE_URL, company, website, getAuthToken } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
import { templates } from '@/stores/websiteTemplatesStore'
export { templates }

export const DEFAULT_TEMPLATE_ID =  'comingSoon01'

// localStorage sometimes wraps string values in JSON quotes (e.g. '"comingSoon01"').
// This strips that extra layer so template lookups always get a bare string.
function normalizeTemplateId(templateId) {
  if (!templateId) {
    return ''
  }

  const value = String(templateId).trim()

  try {
    const parsed = JSON.parse(value)

    return typeof parsed === 'string'
      ? parsed.trim()
      : value
  }
  catch {
    return value
  }
}

export function setWebsiteTemplateId(templateId) {
  const normalizedTemplateId = normalizeTemplateId(templateId)
  const template = templates.find(t => t.id === normalizedTemplateId)

  if (!template) {
    console.warn(`Template not found: ${templateId}`)
    return false
  }

  companyWebsite.templateSelected = normalizedTemplateId
  websiteBuilderState.selectedTemplateId = normalizedTemplateId

  return true
}

export const companyWebsite = reactive({
  templateSelected: DEFAULT_TEMPLATE_ID
})

export const selectedTemplate1 = reactive({})

// Templates load async from the DB so this is null at module evaluation time.
// content/builderContent start as {} and are properly initialised once the
// BuilderView watcher fires after templatesLoaded flips to true.
const defaultTemplate =
  templates.find(t => t.id === companyWebsite.templateSelected) ||
  templates.find(t => t.id === DEFAULT_TEMPLATE_ID) ||
  templates[0] ||
  null

function cloneContent(value) {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value)
    }
    catch {
      // Vue reactive proxies are not structured-cloneable; JSON handles this content shape.
    }
  }

  return JSON.parse(JSON.stringify(value))
}

function isPlainObject(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
  )
}

function isFieldDescriptor(value) {
  return isPlainObject(value) && 'value' in value
}

// Deep-merges saved DB content on top of template defaults.
// Field descriptors ({ type, label, value }) are handled specially: the descriptor
// metadata (type, label) is preserved from the base while only the value is taken
// from saved content. This prevents DB round-trips from stripping builder metadata.
function mergeContent(baseContent, savedContent) {
  if (isFieldDescriptor(baseContent)) {
    if (isFieldDescriptor(savedContent)) {
      return {
        ...cloneContent(baseContent),
        ...cloneContent(savedContent),
        value: mergeContent(baseContent.value, savedContent.value)
      }
    }

    return savedContent == null
      ? cloneContent(baseContent)
      : {
          ...cloneContent(baseContent),
          value: savedContent
        }
  }

  if (Array.isArray(baseContent) || Array.isArray(savedContent)) {
    return cloneContent(
      Array.isArray(savedContent) ? savedContent : baseContent
    )
  }

  if (!isPlainObject(baseContent) || !isPlainObject(savedContent)) {
    return cloneContent(savedContent ?? baseContent)
  }

  const merged = cloneContent(baseContent)

  for (const key of Object.keys(savedContent)) {
    merged[key] = key in baseContent
      ? mergeContent(baseContent[key], savedContent[key])
      : cloneContent(savedContent[key])
  }

  return merged
}

export const content = reactive(defaultTemplate ? cloneContent(defaultTemplate.content) : {})
export const builderContent = reactive(defaultTemplate ? cloneContent(defaultTemplate.content) : {})

function replaceReactiveObject(target, nextContent) {
  Object.keys(target).forEach(key => {
    delete target[key]
  })

  Object.assign(target, cloneContent(nextContent))
}

export function replaceContent(nextContent) {
  replaceReactiveObject(
    content,
    mergeContent(builderContent, nextContent)
  )
}

export function replaceBuilderContent(nextContent) {
  replaceReactiveObject(builderContent, nextContent)
}

export function selectWebsiteTemplate(templateId) {
  const normalizedTemplateId = normalizeTemplateId(templateId)
  const template = templates.find(t => t.id === normalizedTemplateId)
  if (!template) {
    console.warn(`Template not found: ${templateId}`)
    return
  }

  const isNewTemplate = normalizedTemplateId !== websiteBuilderState.selectedTemplateId

  setWebsiteTemplateId(normalizedTemplateId)
  replaceBuilderContent(template.content)

  if (isNewTemplate) {
    replaceContent(template.content)
  }
}

export const websiteBuilderState = reactive({
  selectedTemplateId: companyWebsite.templateSelected,
  businessName: '',
  theme: 'default',
  dirty: false,

  setTemplate(templateId) {
    selectWebsiteTemplate(templateId)
  },

  reset() {
    selectWebsiteTemplate(DEFAULT_TEMPLATE_ID)
    this.businessName = ''
    this.theme = 'default'
  }
})

// Snapshot of the current builder state shared with the iframe preview (SitePreviewView).
// Updated via syncPreviewDraft() after each save or template switch.
export const previewDraft = reactive({
  hasLiveDraft: false,
  websiteId: null,
  companyId: '',
  domain: '',
  quotasticDomain: '',
  siteTitle: '',
  metaDescription: '',
  templateId: websiteBuilderState.selectedTemplateId,
  content: cloneContent(content),
  updatedAt: Date.now()
})

export function syncPreviewDraft({
  website: websiteRecord = {},
  siteTitle = '',
  metaDescription = '',
  templateId = websiteBuilderState.selectedTemplateId,
  nextContent = content
} = {}) {
  previewDraft.hasLiveDraft = true
  previewDraft.websiteId = websiteRecord.id || null
  previewDraft.companyId = websiteRecord.company_id || company.company_id || ''
  previewDraft.domain = websiteRecord.domain || ''
  previewDraft.quotasticDomain =
    websiteRecord.quotastic_domain ||
    website.quotastic_domain ||
    company.company_id ||
    ''
  previewDraft.siteTitle = siteTitle || websiteRecord.site_title || ''
  previewDraft.metaDescription = metaDescription || websiteRecord.meta_description || ''
  previewDraft.templateId = normalizeTemplateId(templateId) || DEFAULT_TEMPLATE_ID
  previewDraft.content = cloneContent(nextContent)
  previewDraft.updatedAt = Date.now()
}

// Save site config to DB
export async function saveWebsite(metadata = {}) {
  try {
    const token = await getAuthToken()
    const response = await fetch(
      `${API_BASE_URL}/sites/draft`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: company.company_id,
          quotastic_domain: website.quotastic_domain,
          ...metadata,
          template_id: websiteBuilderState.selectedTemplateId,
          content
        })
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error)
    }

    getAuthToken().then(token => logAction(token, {
      action: 'website.save',
      label: `Saved draft — template: ${websiteBuilderState.selectedTemplateId}`,
      company_id: company.company_id,
    })).catch(() => {})

    return data
  }
  catch (error) {
    console.error('saveWebsite error:', error)
    throw error
  }
}
